"use client";

import type { Room } from "livekit-client";
import toast from "react-hot-toast";
import { isCapacitor, isTauri, buildApiUrl, getPlatform } from "@/lib/platformUtils";
import { authService } from "@/lib/authService";
import { wsClient } from "@/lib/websocket/TotalAccessWebSocket";
import type {
  CallState,
  CallType,
  IncomingCall,
  OutgoingCallProgress,
  SipCallData,
  ViewMode,
  OperationsUser,
  PickupGroupCall,
  CallReaction,
  GroupCallInfo,
  BreakoutRoom,
  CallParticipant,
  CallActivityEntry,
  AttendedTransferState,
  ScreenPopData,
} from "@/components/call";
import type { DrawingElement } from "@/components/CallWhiteboard";
import type { Dispatch, SetStateAction, MutableRefObject } from "react";

import {
  parseWsInboundEnvelope,
  consumeDeclinedIncomingIfMatched,
  type TotalConnectRtcCallWindowExtension,
} from "@/components/total-connect-rtc/wsInboundEnvelope";
import { parseIncomingCallLikePayload } from "@/components/total-connect-rtc/wsIncomingCallLikePayload";
import { parseIncomingGroupCallPayload } from "@/components/total-connect-rtc/wsIncomingGroupCallPayload";
import {
  parseCallAnsweredPayload,
  parseCallAnswerFailedPayload,
  parseCallEndedOrRejectedPayload,
  parseCallRingingPayload,
  parseSipCallAnsweredPayload,
  parseSipCallEndedPayload,
  parseSipCallRejectedPayload,
} from "@/components/total-connect-rtc/wsCallLifecyclePayload";
import {
  parseSipCallTransferPayload,
  parseSipTransferCancelledPayload,
  parseSipTransferCompletedPayload,
  parseSipTransferFinalizedPayload,
  parseTransferCompletePayload,
  parseTransferInProgressPayload,
  parseTransferRequestPayload,
} from "@/components/total-connect-rtc/wsCallTransferPayload";
import { parseCallMissedPayload } from "@/components/total-connect-rtc/wsCallMissedPayload";
import {
  parsePickupCallConfirmedPayload,
  parsePickupGroupCallLifecyclePayload,
  parsePickupGroupCallRingingPayload,
} from "@/components/total-connect-rtc/wsPickupGroupPayload";
import {
  parseBreakoutRoomsEndedPayload,
  parseBreakoutRoomsStartedPayload,
  parseBreakoutRoomUpdatedPayload,
  parseCallHandRaisePayload,
  parseCallParkedPayload,
  parseCallReactionPayload,
  parseCallRecordingStartedPayload,
  parseCallRecordingStoppedPayload,
  parseCallWhiteboardDrawPayload,
  parseCallWhiteboardStatusPayload,
  parseGuestLeftLobbyPayload,
  parseLobbyRequestPayload,
  parseNewVoicemailPayload,
  parseParkedCallRetrievedPayload,
  parseParticipantMuteRequestPayload,
  parseParticipantRemovedPayload,
} from "@/components/total-connect-rtc/wsCallSessionExtrasPayload";

/** Realtime event types handled elsewhere (messaging, badges) — ignore in call handler. */
export const TOTAL_CONNECT_RTC_WS_IGNORE_TYPES: readonly string[] = [
  "client_threads_list",
  "client_thread_created",
  "thread_messages",
  "new_client_thread_message",
  "message_sent",
  "badge_counts_updated",
  "eligible_members",
  "member_added",
  "member_removed",
  "member_left",
  "added_to_thread",
  "removed_from_thread",
  "client_thread_left",
  "typing",
  "user_typing",
  "messages_read",
  "connected",
];

export type PendingConnection = { host: string; token: string; type: CallType };

export interface TotalConnectRtcWsHandlerContext {
  userId: number | undefined;

  callIdRef: MutableRefObject<string | null>;
  callStateRef: MutableRefObject<CallState>;
  sipCallLogIdRef: MutableRefObject<string | null>;
  sipCallDataRef: MutableRefObject<SipCallData | null>;
  localAnsweringCallIdRef: MutableRefObject<string | null>;
  coldStartHandledRef: MutableRefObject<string | null>;
  lastHandledCallIdRef: MutableRefObject<string | null>;
  isOpenRef: MutableRefObject<boolean>;
  isSipCallRef: MutableRefObject<boolean>;
  isDNDEnabledRef: MutableRefObject<boolean>;
  sipInboundMobileOnlyRef: MutableRefObject<boolean>;
  pendingConnectionRef: MutableRefObject<PendingConnection | null>;
  ringTimeoutRef: MutableRefObject<NodeJS.Timeout | null>;
  callStartTimeRef: MutableRefObject<number | null>;
  missedCallsCountRef: MutableRefObject<number>;
  pendingBlindTransferRef: MutableRefObject<boolean>;
  /** "blind" | "attended" while this user initiated a SIP transfer that is pending. */
  pendingTransferTypeRef: MutableRefObject<string | null>;
  /** True while this session is the *target* of a SIP transfer that hasn't finalized. */
  isTransferTargetRef: MutableRefObject<boolean>;
  secondaryLineOccupiedRef: MutableRefObject<boolean>;
  /** True while call-waiting UI is showing (second SIP offered but not yet answered). */
  secondLinePendingRef: MutableRefObject<boolean>;
  presentSecondLineIncomingRef: MutableRefObject<
    ((call: IncomingCall) => void) | null
  >;
  whiteboardUIModeRef: MutableRefObject<
    | { kind: "idle" }
    | { kind: "presenting" }
    | { kind: "following"; userId: number; userName: string }
  >;

  onClearIncomingCallRef: MutableRefObject<(() => void) | undefined>;
  onIncomingCallRef: MutableRefObject<
    ((call: IncomingCall) => void) | undefined
  >;
  onMissedCallsChangeRef: MutableRefObject<
    ((count: number) => void) | undefined
  >;
  callNotificationsRef: MutableRefObject<{
    stopRingtone: () => void;
    stopDialTone: () => void;
    cancelCallNotification: () => void;
    playRingtone: () => void;
    hapticFeedback: (level: "light" | "medium" | "heavy") => void;
  } | null>;

  answerCallRef: MutableRefObject<() => void>;
  endCallRef: MutableRefObject<
    ((reason?: string) => void | Promise<void>) | null
  >;
  leaveRoomAfterTransferRef: MutableRefObject<(() => void) | null>;
  sipHoldMusicRef: MutableRefObject<
    ((hold: boolean) => Promise<unknown>) | null
  >;
  checkIfBusyRef: MutableRefObject<
    () => Promise<{ isBusy: boolean; reason: string }>
  >;

  callSession: {
    connectToRoom: (
      roomName: string,
      callType: CallType,
      token?: string,
      host?: string,
    ) => Promise<boolean>;
    disconnectFromRoom: (reason: string) => void;
  };
  callControls: {
    toggleMute: () => void;
    toggleHold: () => void;
    isOnHold: boolean;
  };
  roomRef: MutableRefObject<Room | null>;

  setSipCallData: Dispatch<SetStateAction<SipCallData | null>>;
  setIsGateCall: Dispatch<SetStateAction<boolean>>;
  setCallQueue: Dispatch<SetStateAction<IncomingCall[]>>;
  setCallState: Dispatch<SetStateAction<CallState>>;
  setOutgoingCallProgress: (next: OutgoingCallProgress) => void;
  setViewMode: Dispatch<SetStateAction<ViewMode>>;
  setSelectedUser: Dispatch<SetStateAction<OperationsUser | null>>;
  setCallType: Dispatch<SetStateAction<CallType>>;
  setCallDuration: Dispatch<SetStateAction<number>>;
  setPickupGroupCalls: Dispatch<SetStateAction<PickupGroupCall[]>>;
  setShowPickupIndicator: Dispatch<SetStateAction<boolean>>;
  setPickingUpCall: Dispatch<SetStateAction<string | null>>;
  setPendingBlindTransfer: Dispatch<SetStateAction<{ active: boolean }>>;
  setAttendedTransferState: Dispatch<SetStateAction<AttendedTransferState>>;
  setTransferringToExtension: Dispatch<SetStateAction<string | null>>;
  setSipParticipantLocalMute: (muted: boolean) => void;
  setShowTransferDialog: Dispatch<SetStateAction<boolean>>;
  setWhiteboardElements: Dispatch<SetStateAction<DrawingElement[]>>;
  setRemotePresenterInfo: Dispatch<
    SetStateAction<{ userId: number; userName: string } | null>
  >;
  setWhiteboardUIMode: Dispatch<
    SetStateAction<
      | { kind: "idle" }
      | { kind: "presenting" }
      | { kind: "following"; userId: number; userName: string }
    >
  >;
  setShowWhiteboard: Dispatch<SetStateAction<boolean>>;
  setBreakoutRooms: Dispatch<SetStateAction<BreakoutRoom[]>>;
  setIsBreakoutActive: Dispatch<SetStateAction<boolean>>;
  setCurrentBreakoutRoom: Dispatch<SetStateAction<string | null>>;
  setIsGroupCall: Dispatch<SetStateAction<boolean>>;
  setGroupCallInfo: Dispatch<SetStateAction<GroupCallInfo | null>>;
  setParticipantHandRaises: Dispatch<SetStateAction<Map<number, boolean>>>;
  setActiveReactions: Dispatch<SetStateAction<CallReaction[]>>;
  setWaitingParticipants: Dispatch<SetStateAction<CallParticipant[]>>;
  setScreenPopData: Dispatch<SetStateAction<ScreenPopData | null>>;

  markColdStartCallHandling: (callId: string | null | undefined) => void;
  clearColdStartCallHandling: () => void;
  fetchScreenPopData: (phoneNumber: string) => void;
  getLivekitToken: (
    roomName: string,
    type: CallType,
  ) => Promise<{ token: string; host: string } | null>;
  sendWhiteboardStatus: (open: boolean) => void;
  addActivityEntry: (
    activityType: CallActivityEntry["type"],
    participantName: string,
    participantId?: number,
  ) => void;
  loadParkingSlots: () => void | Promise<void>;

  pickingUpCall: string | null;
  showParkingDialog: boolean;
  transferringToExtension: string | null;
  isTauriDesktop: boolean;
  focusWindow: () => void | Promise<void>;
}

/**
 * Call-signaling branch of TotalConnect realtime messages (sip/ops/group/pickup/whiteboard/etc.).
 */
export async function handleTotalConnectRtcWebSocketMessage(
  raw: unknown,
  ctx: TotalConnectRtcWsHandlerContext,
): Promise<void> {
  const envelope = parseWsInboundEnvelope(raw);
  if (!envelope) return;
  if (TOTAL_CONNECT_RTC_WS_IGNORE_TYPES.includes(envelope.type)) {
    return;
  }

  switch (envelope.type) {
    case "sip_incoming_call":
    case "operations_incoming_call":
    case "incoming_call": {
      const p = parseIncomingCallLikePayload(envelope);
      if (!p) break;
      if (p.type === "incoming_call" && !p.is_operations_call) {
        return;
      }

      const incomingSignalCallId = p.call_id;
      if (!incomingSignalCallId) break;

      // ─── Check if call was already declined from FCM notification ───
      // Match room name, signal call id, or call log id — native/JS may flag any of these.
      const declinedIdCandidates = [
        incomingSignalCallId,
        p.call_log_id,
        p.sip_data?.call_log_id,
        p.room_name,
      ];
      if (
        consumeDeclinedIncomingIfMatched(declinedIdCandidates)
      ) {
        const isDeclinedSipCall =
          p.type === "sip_incoming_call" ||
          p.call_type === "sip_inbound" ||
          p.is_sip_call;
        try {
          wsClient.send({
            type: isDeclinedSipCall
              ? "reject_sip_call"
              : "reject_operations_call",
            call_id: incomingSignalCallId,
            ...(isDeclinedSipCall
              ? {
                  call_log_id: p.call_log_id || p.sip_data?.call_log_id,
                }
              : {}),
          });
        } catch (e) {
          console.error(
            "[TotalConnectRTC] Error sending reject for declined call:",
            e,
          );
        }
        return;
      }

      const duplicateIsSipInbound =
        p.type === "sip_incoming_call" ||
        (p.call_type === "sip_inbound" && !!p.sip_data) ||
        !!p.is_sip_call;

      if (duplicateIsSipInbound && ctx.sipInboundMobileOnlyRef?.current) {
        const plat = getPlatform();
        if (plat === "web" || plat === "desktop" || isTauri()) {
          try {
            wsClient.send({
              type: "reject_sip_call",
              call_id: incomingSignalCallId,
              call_log_id: p.call_log_id || p.sip_data?.call_log_id,
            });
          } catch (e) {
            console.error(
              "[TotalConnectRTC] Error sending reject (SIP ring mobile-only):",
              e,
            );
          }
          return;
        }
      }

      if (
        ctx.callIdRef.current === incomingSignalCallId &&
        ["ringing", "connecting", "connected", "reconnecting"].includes(
          ctx.callStateRef.current,
        )
      ) {
        if (duplicateIsSipInbound) {
          const mergedSipData: SipCallData = {
            ...(ctx.sipCallDataRef.current || {}),
            ...(p.sip_data || {}),
            client_id: p.client_id || ctx.sipCallDataRef.current?.client_id,
            call_log_id:
              p.call_log_id ||
              p.sip_data?.call_log_id ||
              ctx.sipCallLogIdRef.current ||
              ctx.sipCallDataRef.current?.call_log_id,
            livekit_room:
              p.room_name ||
              p.sip_data?.livekit_room ||
              ctx.sipCallDataRef.current?.livekit_room ||
              incomingSignalCallId,
            livekit_token:
              p.sip_data?.livekit_token ||
              ctx.sipCallDataRef.current?.livekit_token,
            livekit_host:
              p.sip_data?.livekit_host ||
              ctx.sipCallDataRef.current?.livekit_host,
            caller_number:
              p.caller_id_number ||
              p.caller_number ||
              ctx.sipCallDataRef.current?.caller_number,
          };

          ctx.setSipCallData(mergedSipData);
          ctx.isSipCallRef.current = true;
          ctx.sipCallLogIdRef.current = mergedSipData.call_log_id || null;
        }

        return;
      }

      // ─── Busy detection ───
      // If user is actively connected, queue the incoming call instead of rejecting
      const activeStates: CallState[] = [
        "connected",
        "connecting",
        "reconnecting",
      ];

      const inboundIsSipForDualLine =
        p.type === "sip_incoming_call" ||
        (p.call_type === "sip_inbound" && !!p.sip_data) ||
        !!p.is_sip_call;

      const sipDualLineSessionEligible =
        ctx.isSipCallRef.current &&
        activeStates.includes(ctx.callStateRef.current);

      /** Offer call waiting when primary SIP session is active and second slot is free. */
      if (
        sipDualLineSessionEligible &&
        inboundIsSipForDualLine &&
        !ctx.secondaryLineOccupiedRef.current &&
        !ctx.secondLinePendingRef.current &&
        !ctx.pendingBlindTransferRef.current
      ) {
        const callerIdNumber =
          p.caller_id_number || p.caller_number || "";
        const roomName = p.room_name || incomingSignalCallId;
        const sipPayload: IncomingCall = {
          call_id: incomingSignalCallId,
          caller_id: p.caller_id || 0,
          caller_name:
            p.caller_name || p.caller_id_name || callerIdNumber || "Unknown",
          caller_avatar: p.caller_avatar,
          call_type: "voice",
          client_id: p.client_id,
          timestamp: new Date().toISOString(),
          isSipCall: true,
          caller_number: callerIdNumber,
          sipData: {
            ...p.sip_data,
            client_id: p.client_id,
            call_log_id: p.call_log_id || p.sip_data?.call_log_id,
            livekit_room: roomName,
            livekit_token: p.sip_data?.livekit_token,
            livekit_host: p.sip_data?.livekit_host,
            caller_number: callerIdNumber,
          },
        };
        ctx.presentSecondLineIncomingRef.current?.(sipPayload);
        ctx.callNotificationsRef.current?.playRingtone();
        break;
      }

      /** Both SIP lines in use (active second leg or unanswered call waiting): reject inbound SIP. */
      if (
        activeStates.includes(ctx.callStateRef.current) &&
        inboundIsSipForDualLine &&
        ctx.isSipCallRef.current &&
        (ctx.secondaryLineOccupiedRef.current ||
          ctx.secondLinePendingRef.current)
      ) {
        try {
          wsClient.send({
            type: "reject_sip_call",
            call_id: incomingSignalCallId,
            call_log_id: p.call_log_id || p.sip_data?.call_log_id,
          });
        } catch (e) {
          console.error(
            "[TotalConnectRTC] reject_sip_call (dual-line busy):",
            e,
          );
        }
        return;
      }

      if (activeStates.includes(ctx.callStateRef.current)) {
        const queuedCallType: CallType =
          p.call_type === "video" ? "video" : "voice";
        const queuedIsSip =
          p.type === "sip_incoming_call" ||
          (p.call_type === "sip_inbound" && !!p.sip_data) ||
          !!p.is_sip_call;
        const queuedCallerNumber =
          p.caller_id_number || p.caller_number || "";
        const queuedCall: IncomingCall = {
          call_id: incomingSignalCallId,
          caller_id: p.caller_id || 0,
          caller_name: p.caller_name || "Unknown",
          caller_avatar: p.caller_avatar,
          call_type: queuedCallType,
          client_id: p.client_id,
          timestamp: new Date().toISOString(),
          isSipCall: queuedIsSip || undefined,
          isGateCall: p.gate_call || undefined,
          caller_number: queuedCallerNumber || undefined,
          sipData: queuedIsSip
            ? {
                ...p.sip_data,
                client_id: p.client_id,
                call_log_id: p.call_log_id || p.sip_data?.call_log_id,
                livekit_room: p.room_name || incomingSignalCallId,
                livekit_token: p.sip_data?.livekit_token,
                livekit_host: p.sip_data?.livekit_host,
                caller_number: queuedCallerNumber,
              }
            : undefined,
        };
        ctx.setCallQueue((prev) => {
          // Don't queue duplicates
          if (prev.some((c) => c.call_id === incomingSignalCallId)) return prev;
          return [...prev, queuedCall];
        });
        // Notify caller we're busy but call is queued (don't fully reject)
        wsClient.send({
          type: "call_declined",
          call_id: incomingSignalCallId,
          reason: "busy",
          busy_reason: "livekit",
          target_user_id: p.caller_id,
        });
        return;
      }

      // ─── DND check ───
      // If Do Not Disturb is enabled, silently decline the call
      if (ctx.isDNDEnabledRef.current) {
        wsClient.send({
          type: "call_declined",
          call_id: incomingSignalCallId,
          reason: "dnd",
          target_user_id: p.caller_id,
        });
        return;
      }
      // ─── End DND check ───

      // Clean up stale non-active states (ringing from previous missed call, initiating, etc.)
      if (
        ctx.callStateRef.current !== "idle" &&
        ctx.callStateRef.current !== "ended"
      ) {
        ctx.callNotificationsRef.current?.stopRingtone();
        ctx.callNotificationsRef.current?.stopDialTone();
        if (ctx.ringTimeoutRef.current) {
          clearTimeout(ctx.ringTimeoutRef.current);
          ctx.ringTimeoutRef.current = null;
        }
        ctx.callIdRef.current = null;
        ctx.pendingConnectionRef.current = null;
        ctx.setCallState("idle");
        ctx.callStateRef.current = "idle";
        ctx.onClearIncomingCallRef.current?.();
      }

      // CRITICAL: Check if user already tapped "Answer" on FCM notification
      // before we clear the pending answer data. If so, we'll auto-answer after setup.
      let shouldAutoAnswer = false;
      if (typeof window !== "undefined") {
        const pendingAnswer = (window as TotalConnectRtcCallWindowExtension)
          .__pendingCallAnswer;
        if (
          pendingAnswer &&
          (pendingAnswer.callId === incomingSignalCallId ||
            pendingAnswer.call_id === incomingSignalCallId)
        ) {
          shouldAutoAnswer = !!pendingAnswer.autoAnswer;
          (window as TotalConnectRtcCallWindowExtension).__pendingCallAnswer =
            null;
          try {
            localStorage.removeItem("coldstart_pending_call");
            sessionStorage.removeItem("pending_call_answer");
            localStorage.removeItem("coldstart_call_handling");
          } catch (e) {}
        }

        // Also check sessionStorage for pending answer (set by Android native)
        if (!shouldAutoAnswer) {
          try {
            const storedAnswer = sessionStorage.getItem("pending_call_answer");
            if (storedAnswer) {
              const parsed = JSON.parse(storedAnswer);
              if (
                parsed.callId === incomingSignalCallId ||
                parsed.call_id === incomingSignalCallId
              ) {
                shouldAutoAnswer = parsed.autoAnswer === true;
                sessionStorage.removeItem("pending_call_answer");
              }
            }
          } catch (e) {}
        }
      }
      // Also mark in ctx.coldStartHandledRef to prevent the usePendingCall hook from re-triggering
      ctx.coldStartHandledRef.current = incomingSignalCallId;
      ctx.lastHandledCallIdRef.current = incomingSignalCallId;
      // Add to window-level handled set (survives HMR)
      if (typeof window !== "undefined") {
        const w = window as TotalConnectRtcCallWindowExtension;
        if (!w.__handledCallIds) {
          w.__handledCallIds = new Set<string>();
        }
        w.__handledCallIds.add(incomingSignalCallId);
      }
      // DO NOT call confirmColdStartHandled here — defer until call actually
      // CONNECTS to LiveKit room. If we mark handled now and the page navigates
      // (e.g., auth redirect during cold start), the call data becomes invisible
      // on subsequent page loads, leaving the user on the dashboard.
      // Same-page dedup is handled by ctx.coldStartHandledRef + ctx.lastHandledCallIdRef +
      // __handledCallIds above.

      // Check if user is on a cellular call (PhoneState plugin)
      try {
        const busyCheck = await ctx.checkIfBusyRef.current();
        if (busyCheck.isBusy) {
          wsClient.send({
            type: "call_declined",
            call_id: incomingSignalCallId,
            reason: "busy",
            busy_reason: busyCheck.reason,
            target_user_id: p.caller_id,
          });
          return;
        }
      } catch (e) {}
      // ─── End busy detection ───

      // Detect SIP inbound call
      const isSipInbound =
        p.type === "sip_incoming_call" ||
        (p.call_type === "sip_inbound" && !!p.sip_data) ||
        !!p.is_sip_call;

      if (isSipInbound) {
        const sipDataToSet = {
          ...p.sip_data,
          client_id: p.client_id,
          call_log_id: p.call_log_id || p.sip_data?.call_log_id,
          livekit_room: p.room_name || incomingSignalCallId,
          livekit_token: p.sip_data?.livekit_token,
          livekit_host: p.sip_data?.livekit_host,
          caller_number: p.caller_id_number || p.caller_number,
        };
        ctx.setSipCallData(sipDataToSet);
        ctx.isSipCallRef.current = true;
        ctx.sipCallLogIdRef.current =
          p.call_log_id || p.sip_data?.call_log_id || null;

        // Cache sipCallData in localStorage so a new session after page reload
        // can use it even if the WS message was consumed by this session
        try {
          localStorage.setItem(
            "sip_call_data_cache",
            JSON.stringify({
              call_id: incomingSignalCallId,
              sip_data: sipDataToSet,
              timestamp: Date.now(),
            }),
          );
        } catch (e) {}

        // Screen Pop: CRM contact lookup for inbound SIP
        const callerPhone = p.caller_id_number || p.caller_number || "";
        if (callerPhone) {
          ctx.fetchScreenPopData(callerPhone);
        }
      } else {
        ctx.setSipCallData(null);
        ctx.setScreenPopData(null);
        ctx.isSipCallRef.current = false;
        ctx.sipCallLogIdRef.current = null;
      }

      // Visitor-management gate calls surface authorize/deny on the callee UI
      ctx.setIsGateCall(!!p.gate_call);

      const callerId = p.caller_id || 0;
      const callerIdNumber = p.caller_id_number || p.caller_number || "";
      const callerIdName = p.caller_id_name || p.caller_name || callerIdNumber;
      const callId = incomingSignalCallId;
      const roomName = p.room_name || incomingSignalCallId;
      const incomingCallType: CallType = isSipInbound
        ? "voice"
        : p.call_type === "video"
          ? "video"
          : "voice";

      // If modal not open, notify parent
      if (!ctx.isOpenRef.current && ctx.onIncomingCallRef.current) {
        ctx.onIncomingCallRef.current({
          call_id: callId,
          caller_id: callerId,
          caller_name: isSipInbound
            ? `📞 ${callerIdName}`
            : p.caller_name || "Unknown",
          caller_avatar: p.caller_avatar,
          call_type: incomingCallType,
          client_id: p.client_id,
          timestamp: new Date().toISOString(),
          isSipCall: isSipInbound,
          isGateCall: p.gate_call || undefined,
          caller_number: callerIdNumber,
          sipData: isSipInbound
            ? {
                ...p.sip_data,
                livekit_token: p.sip_data?.livekit_token,
                livekit_host: p.sip_data?.livekit_host,
                livekit_room: roomName,
                call_log_id: p.call_log_id || p.sip_data?.call_log_id,
              }
            : undefined,
        });
      }

      ctx.callIdRef.current = callId;
      ctx.setSelectedUser({
        id: callerId,
        email: "",
        first_name: isSipInbound
          ? callerIdName || "Incoming Call"
          : p.caller_name?.split(" ")[0] || "Unknown",
        last_name: isSipInbound
          ? callerIdNumber || ""
          : p.caller_name?.split(" ")[1] || "",
        role: isSipInbound ? "External" : "",
        module_role: "",
        avatar_url: p.caller_avatar,
      });
      ctx.setCallType(incomingCallType);
      ctx.setViewMode("incall");
      ctx.setCallState("ringing");
      ctx.callStateRef.current = "ringing";

      // If user already tapped "Answer" on FCM notification, auto-answer immediately
      if (shouldAutoAnswer) {
        ctx.markColdStartCallHandling(callId);
        // COLD START FIX: Wait for infrastructure to be ready before auto-answering
        // Check WebSocket connection and retry with backoff to prevent stuck "Connecting..." state
        const waitForReadyAndAnswer = (attempt = 0) => {
          const MAX_ATTEMPTS = 20; // 20 attempts × 500ms = 10 seconds max
          const INITIAL_DELAY = attempt === 0 ? 1200 : 500; // First attempt waits 1.2s for LiveKit init

          if (
            ctx.callStateRef.current !== "ringing" ||
            ctx.callIdRef.current !== callId
          ) {
            return;
          }

          if (attempt >= MAX_ATTEMPTS) {
            console.warn(
              "[TotalConnectRTC] Auto-answer timeout - infrastructure not ready after 10s",
            );
            // Still try to answer anyway as last resort
            ctx.answerCallRef.current();
            return;
          }

          // Check if WebSocket is connected (LiveKit needs WS for signaling)
          if (wsClient.isConnected()) {
            // Extra 200ms buffer for LiveKit SDK internal setup
            setTimeout(() => {
              if (
                ctx.callStateRef.current === "ringing" &&
                ctx.callIdRef.current === callId
              ) {
                ctx.answerCallRef.current();
              }
            }, 200);
          } else {
            setTimeout(() => waitForReadyAndAnswer(attempt + 1), INITIAL_DELAY);
          }
        };

        waitForReadyAndAnswer();
      } else {
        ctx.callNotificationsRef.current?.playRingtone();
      }

      // Client-side ringing timeout safety net (server also has 45s timeout)
      // If no answer/end signal received within 50s, stop ringing
      if (ctx.ringTimeoutRef.current) clearTimeout(ctx.ringTimeoutRef.current);
      ctx.ringTimeoutRef.current = setTimeout(() => {
        if (ctx.callStateRef.current === "ringing") {
          ctx.callNotificationsRef.current?.stopRingtone();
          ctx.callNotificationsRef.current?.stopDialTone();
          ctx.localAnsweringCallIdRef.current = null;
          ctx.clearColdStartCallHandling();
          ctx.setCallState("idle");
          ctx.callStateRef.current = "idle";
          ctx.setViewMode("contacts");
          ctx.setSelectedUser(null);
          ctx.callIdRef.current = null;
          ctx.onClearIncomingCallRef.current?.();
        }
      }, 50000);

      // Focus window on Tauri desktop
      if (ctx.isTauriDesktop) {
        ctx.focusWindow();
      }
      break;
    }

    case "call_answered": {
      const p = parseCallAnsweredPayload(envelope);
      if (!p) break;
      const answeredCallId = p.call_id || ctx.callIdRef.current;
      const answererId = p.answerer_id;
      const isLocalAnswerEcho = !!(
        answeredCallId &&
        answererId &&
        answererId === ctx.userId &&
        ctx.localAnsweringCallIdRef.current === answeredCallId
      );

      // If the server explicitly tells us this was answered on another device, stop ringing.
      // Also handle legacy: if a different user answered (e.g., ring group),
      // or if same user answered on another session (answerer_id matches but
      // answered_on_other_device is set).
      if (ctx.callStateRef.current === "ringing") {
        // If answered_on_other_device flag is explicitly set, always stop ringing
        if (p.answered_on_other_device) {
          if (answeredCallId) {
            ctx.lastHandledCallIdRef.current = answeredCallId;
            if (typeof window !== 'undefined') {
              const w = window as TotalConnectRtcCallWindowExtension;
              if (!w.__handledCallIds) w.__handledCallIds = new Set<string>();
              w.__handledCallIds.add(answeredCallId);
            }
          }
          if (ctx.ringTimeoutRef.current) {
            clearTimeout(ctx.ringTimeoutRef.current);
            ctx.ringTimeoutRef.current = null;
          }
          ctx.onClearIncomingCallRef.current?.();
          if (isLocalAnswerEcho || p.answered_on_this_device) {
            ctx.callNotificationsRef.current?.stopRingtone();
            ctx.callNotificationsRef.current?.cancelCallNotification();
            break;
          }
          ctx.callNotificationsRef.current?.stopRingtone();
          ctx.callNotificationsRef.current?.cancelCallNotification();
          ctx.endCallRef.current?.("answered_on_other_device");
          break;
        }
        // Legacy: check if a different user answered (e.g., caller receiving answer notification)
        if (answererId && answererId === ctx.userId) {
          break;
        }
        // Different user answered (e.g., ring group member)
        ctx.callNotificationsRef.current?.stopRingtone();
        ctx.callNotificationsRef.current?.cancelCallNotification();
        ctx.endCallRef.current?.("answered_on_other_device");
        break;
      }

      if (ctx.callStateRef.current === "initiating") {
        ctx.setCallState("connecting");
        ctx.setOutgoingCallProgress("connecting");
        ctx.callStateRef.current = "connecting";
        ctx.callNotificationsRef.current?.stopDialTone();

        // If we have pending connection data, connect now
        if (ctx.pendingConnectionRef.current) {
          const { host, token, type } = ctx.pendingConnectionRef.current;
          ctx.pendingConnectionRef.current = null;
          ctx.callSession
            .connectToRoom(ctx.callIdRef.current!, type, token, host)
            .then((connected) => {
              if (connected) {
                ctx.setCallState("connected");
                ctx.callStateRef.current = "connected";
                ctx.callStartTimeRef.current = Date.now();
              }
            });
        }
      }
      break;
    }

    case "call_ringing": {
      const p = parseCallRingingPayload(envelope);
      if (!p) break;

      const ringingCallId = p.call_id || ctx.callIdRef.current;
      const isCurrentOutgoingCall =
        !!ringingCallId &&
        ringingCallId === ctx.callIdRef.current &&
        (ctx.callStateRef.current === 'initiating' ||
          ctx.callStateRef.current === 'connecting');

      if (!isCurrentOutgoingCall) {
        break;
      }

      const recipientReached =
        p.ws_notified || p.fcm_sent || p.fcm_requested || p.ws_in_foreground;

      if (recipientReached) {
        ctx.setOutgoingCallProgress('ringing');
      }

      break;
    }

    // SIP-specific events
    case "sip_call_answered": {
      const p = parseSipCallAnsweredPayload(envelope);
      if (!p) break;
      const sipAnsweredCallId =
        p.call_id || p.livekit_room || p.room_name || ctx.callIdRef.current;
      const isLocalSipAnswerEcho = !!(
        sipAnsweredCallId &&
        p.answerer_id === ctx.userId &&
        ctx.localAnsweringCallIdRef.current === sipAnsweredCallId
      );

      if (p.answered_on_this_device || isLocalSipAnswerEcho) {
        ctx.callNotificationsRef.current?.stopRingtone();
        ctx.callNotificationsRef.current?.cancelCallNotification();
      }

      if (p.answered_on_other_device) {
        if (sipAnsweredCallId) {
          ctx.lastHandledCallIdRef.current = sipAnsweredCallId;
          if (typeof window !== 'undefined') {
            const w = window as TotalConnectRtcCallWindowExtension;
            if (!w.__handledCallIds) w.__handledCallIds = new Set<string>();
            w.__handledCallIds.add(sipAnsweredCallId);
          }
        }
        if (ctx.ringTimeoutRef.current) {
          clearTimeout(ctx.ringTimeoutRef.current);
          ctx.ringTimeoutRef.current = null;
        }
        ctx.onClearIncomingCallRef.current?.();

        if (isLocalSipAnswerEcho || p.answered_on_this_device) {
          break;
        }

        // Always stop ringtone and cancel notifications regardless of state
        ctx.callNotificationsRef.current?.stopRingtone();
        ctx.callNotificationsRef.current?.cancelCallNotification();

        // Only end the call UI if we're still ringing (not the device that answered)
        if (ctx.callStateRef.current === "ringing") {
          toast.success("Call answered on another device", { duration: 3000 });
          ctx.endCallRef.current?.("answered_on_other_device");
        }
      }
      break;
    }

    case "sip_call_rejected": {
      const p = parseSipCallRejectedPayload(envelope);
      if (!p) break;
      if (p.rejected_on_other_device) {
        const rejectedCallId = p.call_id || ctx.callIdRef.current;
        if (rejectedCallId) {
          ctx.lastHandledCallIdRef.current = rejectedCallId;
          if (typeof window !== 'undefined') {
            const w = window as TotalConnectRtcCallWindowExtension;
            if (!w.__handledCallIds) w.__handledCallIds = new Set<string>();
            w.__handledCallIds.add(rejectedCallId);
          }
        }
        if (ctx.ringTimeoutRef.current) {
          clearTimeout(ctx.ringTimeoutRef.current);
          ctx.ringTimeoutRef.current = null;
        }
        ctx.onClearIncomingCallRef.current?.();
        ctx.callNotificationsRef.current?.stopRingtone();
        ctx.callNotificationsRef.current?.cancelCallNotification();
        if (ctx.callStateRef.current === "ringing") {
          ctx.endCallRef.current?.("rejected_on_other_device");
        }
      }
      break;
    }

    case "sip_call_transfer": {
      const p = parseSipCallTransferPayload(envelope);
      if (!p) break;

      if (
        ctx.callStateRef.current !== "idle" &&
        ctx.callStateRef.current !== "ended"
      ) {
        toast.error("Cannot accept transfer - already in a call");
        // Decline the transfer server-side so the initiator's call returns
        // immediately instead of waiting for the ring timeout.
        if (wsClient.isConnected()) {
          wsClient.send({
            type: "reject_sip_call",
            call_id: p.call_id,
            call_log_id: p.call_log_id,
          });
        }
        break;
      }

      // Without LiveKit credentials the target can't join — decline so the
      // initiator's call returns instead of ringing into the void.
      if (!p.livekit_token || !p.livekit_host) {
        toast.error("Cannot accept transfer - connection details missing");
        if (wsClient.isConnected()) {
          wsClient.send({
            type: "reject_sip_call",
            call_id: p.call_id,
            call_log_id: p.call_log_id,
          });
        }
        break;
      }

      // Set up incoming transfer — include token/host for fast room join.
      // Marked as transfer target so local end-call paths never tear down
      // the original PSTN leg while the transfer is pending.
      ctx.isTransferTargetRef.current = true;
      ctx.callIdRef.current = p.call_id;
      ctx.isSipCallRef.current = true;
      ctx.sipCallLogIdRef.current = p.call_log_id || null;
      ctx.setSipCallData({
        call_log_id: p.call_log_id,
        livekit_room: p.livekit_room,
        livekit_token: p.livekit_token,
        livekit_host: p.livekit_host,
        client_id: p.client_id,
      });

      ctx.setSelectedUser({
        id: p.from_user_id || 0,
        email: "",
        first_name: `Transfer: ${p.caller_name || p.caller_number || "Unknown"}`,
        last_name: "",
        role: "",
        module_role: "",
      });

      ctx.setCallType("voice");
      ctx.setViewMode("incall");
      ctx.setCallState("ringing");
      ctx.callStateRef.current = "ringing";
      ctx.callNotificationsRef.current?.playRingtone();

      ctx.onIncomingCallRef.current?.({
        caller_id: p.from_user_id || 0,
        caller_name: `📲 Transfer: ${p.caller_name || p.caller_number}`,
        call_type: "voice",
        call_id: p.call_id,
        isSipCall: true,
      });
      break;
    }

    case "transfer_request": {
      const p = parseTransferRequestPayload(envelope);
      if (!p) break;

      // Device-to-device transfer: same user moving their own call to this session
      if (ctx.callStateRef.current !== "idle") {
        console.warn(
          "[TotalConnectRTC] Cannot accept device transfer — already in a call",
        );
        break;
      }
      if (!p.livekit_token || !p.livekit_host) {
        console.error(
          "[TotalConnectRTC] Device transfer missing LiveKit credentials",
        );
        break;
      }

      // Set up call state before connecting
      const roomName =
        p.livekit_room ||
        (p.call_id.startsWith("ops_call_")
          ? p.call_id
          : `ops_call_${p.call_id}`);
      ctx.callIdRef.current = p.call_id;
      const transferCallType = p.call_type === "video" ? "video" : "voice";
      ctx.setCallType(transferCallType);
      ctx.setViewMode("incall");
      ctx.setCallState("connecting");
      ctx.callStateRef.current = "connecting";

      // Detect SIP call from flag or call_id prefix
      const transferIsSip =
        p.is_sip_call ||
        p.call_id.startsWith("sip-") ||
        p.call_id.startsWith("sip_");
      if (transferIsSip) {
        ctx.isSipCallRef.current = true;
        ctx.sipCallLogIdRef.current = p.sip_call_log_id || null;
        ctx.setSipCallData({
          call_log_id: p.sip_call_log_id || undefined,
          provider_call_id: p.provider_call_id || undefined,
          livekit_room: p.livekit_room || roomName,
          livekit_token: p.livekit_token,
          livekit_host: p.livekit_host,
          client_id: p.client_id,
        });
      }

      // Ensure the call modal is open
      if (!ctx.isOpenRef.current && ctx.onIncomingCallRef.current) {
        ctx.onIncomingCallRef.current({
          call_id: p.call_id,
          caller_id: p.target_user_id || p.caller_id || 0,
          caller_name: "📲 Device Transfer",
          call_type: transferCallType,
        });
      }

      ctx.setSelectedUser({
        id: p.target_user_id || p.caller_id || 0,
        email: "",
        first_name: p.target_user_name || p.caller_name || "Transferred Call",
        last_name: "",
        role: "",
        module_role: "",
      });

      try {
        const connected = await ctx.callSession.connectToRoom(
          roomName,
          transferCallType,
          p.livekit_token,
          p.livekit_host,
        );
        if (!connected)
          throw new Error("Failed to connect to transferred room");

        ctx.setCallState("connected");
        ctx.callStateRef.current = "connected";
        ctx.callStartTimeRef.current = Date.now();
        toast.success("📲 Call transferred to this device");

        // Notify server that transfer was accepted
        try {
          const tokens = authService.getTokens();
          await fetch(buildApiUrl("/api/v1/calls"), {
            method: "POST",
            headers: {
              Authorization: `Bearer ${tokens?.access_token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              action: "transfer_accept",
              call_id: p.call_id,
              platform: isCapacitor()
                ? "capacitor"
                : isTauri()
                  ? "desktop"
                  : "web",
              session_id: authService.getSessionId(),
            }),
          });
        } catch (acceptErr) {
          console.error(
            "[TotalConnectRTC] Failed to notify transfer accept:",
            acceptErr,
          );
        }
      } catch (err) {
        console.error("[TotalConnectRTC] Device transfer connect failed:", err);
        toast.error("Failed to connect transferred call");
        ctx.setCallState("idle");
        ctx.callStateRef.current = "idle";
        ctx.setViewMode("contacts");
        ctx.callIdRef.current = null;
      }

      break;
    }

    case "transfer_in_progress": {
      const p = parseTransferInProgressPayload(envelope);
      if (!p) break;
      if (p.user_id !== ctx.userId) {
        toast(
          p.message ||
            "Participant is switching devices - keeping the call open",
        );
      }
      break;
    }

    case "transfer_complete": {
      const p = parseTransferCompletePayload(envelope);
      if (!p) break;
      if (p.user_id === ctx.userId && p.call_id === ctx.callIdRef.current) {
        ctx.endCallRef.current?.("transferred_to_device");
      }
      break;
    }

    case "sip_transfer_timeout":
      if (ctx.callStateRef.current === "ringing") {
        ctx.callNotificationsRef.current?.stopRingtone();
        toast("Transfer timed out", { icon: "⏱️" });
        ctx.endCallRef.current?.("transfer_timeout");
      }
      break;

    case "sip_transfer_returned": {
      toast.dismiss("blind-transfer-pending");
      ctx.pendingTransferTypeRef.current = null;
      const returnReason =
        typeof envelope.reason === "string" ? envelope.reason : "";
      const returnMsg =
        returnReason === "cancelled"
          ? "Transfer cancelled - call returned to you"
          : returnReason === "target_left"
            ? "Transfer target left - call returned to you"
            : "Transfer not answered - call returned to you";

      if (
        ctx.pendingBlindTransferRef.current &&
        ctx.callStateRef.current === "connected" &&
        ctx.roomRef.current
      ) {
        ctx.callNotificationsRef.current?.hapticFeedback("medium");
        toast.success(returnMsg);

        ctx.setPendingBlindTransfer({ active: false });
        ctx.pendingBlindTransferRef.current = false;
        ctx.setAttendedTransferState({ status: "idle" });
        ctx.setTransferringToExtension(null);

        // Unmute SIP participant audio locally, stop server-side MoH
        // (attended path), and resume client-side hold (blind path).
        ctx.setSipParticipantLocalMute(false);
        void ctx.sipHoldMusicRef.current?.(false);
        if (ctx.callControls.isOnHold) {
          ctx.callControls.toggleHold();
        }
        break;
      }

      // Attended transfer returned
      if (ctx.callStateRef.current === "connected" && ctx.roomRef.current) {
        toast.success(returnMsg);
        ctx.setAttendedTransferState({ status: "idle" });
        ctx.setTransferringToExtension(null);
        ctx.setPendingBlindTransfer({ active: false });
        ctx.pendingBlindTransferRef.current = false;

        ctx.setSipParticipantLocalMute(false);
        void ctx.sipHoldMusicRef.current?.(false);
        if (ctx.callControls.isOnHold) {
          ctx.callControls.toggleHold();
        }
      }
      break;
    }

    case "sip_transfer_completed": {
      const p = parseSipTransferCompletedPayload(envelope);
      if (!p) break;

      toast.dismiss("blind-transfer-pending");
      const transferType =
        p.transfer_type ?? ctx.pendingTransferTypeRef.current;
      ctx.pendingTransferTypeRef.current = null;

      if (ctx.callStateRef.current !== "connected") break;

      // Blind transfer: the initiator's job is done once the target answers —
      // release the hold and leave the room WITHOUT terminating the SIP leg.
      // The target keeps the PSTN caller.
      if (transferType === "blind") {
        const targetName =
          p.target_name ?? ctx.transferringToExtension ?? "transfer target";
        toast.success(`Transferred to ${targetName}`);
        ctx.setPendingBlindTransfer({ active: false });
        ctx.pendingBlindTransferRef.current = false;
        ctx.setAttendedTransferState({ status: "idle" });
        ctx.setShowTransferDialog(false);
        // Release the server-side hold/MoH before leaving so the caller isn't
        // stuck on hold music.
        void ctx.sipHoldMusicRef.current?.(false);
        if (ctx.callControls.isOnHold) {
          ctx.callControls.toggleHold();
        }
        ctx.leaveRoomAfterTransferRef.current?.();
        break;
      }

      // Attended transfer: target answered — enter consultation phase.
      // User A can now talk to User B while the external caller stays on hold.
      // User A can either complete the transfer (leave room) or cancel (remove User B).
      const targetName =
        p.target_name ?? ctx.transferringToExtension ?? "transfer target";
      toast.success(
        `${targetName} answered — you can now consult before completing the transfer`,
      );

      ctx.setAttendedTransferState({
        status: "consulting",
        targetExtension: p.target_extension,
        targetName:
          typeof targetName === "string" ? targetName : String(targetName),
      });

      // Hide the pending transfer overlay since we're now in consultation mode
      ctx.setPendingBlindTransfer({ active: false });
      ctx.pendingBlindTransferRef.current = false;
      ctx.setShowTransferDialog(false);

      // NOTE: Do NOT end the call here. User A stays in the room to consult
      // with User B. The external caller remains on hold with MOH.
      break;
    }

    case "sip_transfer_cancelled": {
      const p = parseSipTransferCancelledPayload(envelope);
      if (!p) break;

      // Sent to the transfer *target* when the initiator cancels (or a
      // server-side transfer rejection fires). Never terminate the SIP leg —
      // endCall skips SIP teardown for transfer_cancelled.
      ctx.callNotificationsRef.current?.stopRingtone();
      ctx.callNotificationsRef.current?.cancelCallNotification();
      toast(p.reason === "call_ended" ? "Call ended" : "Transfer cancelled", {
        icon: "📞",
      });
      ctx.isTransferTargetRef.current = false;
      if (ctx.callStateRef.current !== "idle") {
        ctx.endCallRef.current?.("transfer_cancelled");
      }
      break;
    }

    case "sip_transfer_finalized": {
      const p = parseSipTransferFinalizedPayload(envelope);
      if (!p) break;

      // The transfer fully handed off — this session now owns the SIP leg,
      // so a later hangup terminates the call normally.
      ctx.isTransferTargetRef.current = false;
      toast.success("Call transferred to you");
      break;
    }

    case "sip_call_ended": {
      const p = parseSipCallEndedPayload(envelope);
      if (!p) break;
      const endedLog =
        p.call_log_id != null ? String(p.call_log_id) : undefined;
      const refLog = ctx.sipCallLogIdRef.current;
      const logMatches =
        !!endedLog && !!refLog && endedLog === String(refLog);
      const isOurSipCall =
        logMatches ||
        (ctx.callIdRef.current &&
          (p.livekit_room === ctx.callIdRef.current ||
            p.room_name === ctx.callIdRef.current ||
            p.call_id === ctx.callIdRef.current));

      if (isOurSipCall && ctx.isSipCallRef.current) {
        if (p.was_on_hold) {
          toast("Caller hung up while on hold", { icon: "📞" });
        } else {
          toast("External caller hung up", { icon: "📞" });
        }
        ctx.endCallRef.current?.("sip_remote_hangup");
      }
      break;
    }

    case "call_rejected":
    case "call_ended": {
      const p = parseCallEndedOrRejectedPayload(envelope);
      if (!p) break;
      if (ctx.callStateRef.current !== "idle") {
        ctx.localAnsweringCallIdRef.current = null;
        ctx.clearColdStartCallHandling();
        // Ignore 'already_ended' for connected/connecting calls — this just means
        // the server's call record was already cleaned up (e.g., by an earlier answer/reject msg).
        // Our local LiveKit connection is the source of truth for active calls.
        if (
          (p.reason === "already_ended" || p.reason === "not_found") &&
          (ctx.callStateRef.current === "connected" ||
            ctx.callStateRef.current === "connecting")
        ) {
          break;
        }
        // For connected calls, check if we still have participants
        if (ctx.callStateRef.current === "connected" && ctx.roomRef.current) {
          if (ctx.roomRef.current.remoteParticipants.size > 0) {
            break;
          }
        }
        ctx.callNotificationsRef.current?.stopRingtone();
        ctx.callNotificationsRef.current?.stopDialTone();
        ctx.setOutgoingCallProgress('idle');

        // Clean up server-side participant record via REST API
        const endedCallId = ctx.callIdRef.current || p.call_id;
        if (endedCallId) {
          ctx.lastHandledCallIdRef.current = endedCallId;
          const tokens = authService.getTokens();
          fetch(buildApiUrl("/api/v1/calls"), {
            method: "POST",
            headers: {
              Authorization: `Bearer ${tokens?.access_token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              action: "end",
              call_id: endedCallId,
              reason: "ws_call_ended",
            }),
          }).catch((err) =>
            console.error(
              "[TotalConnectRTC] Failed to clean up ended call record:",
              err,
            ),
          );
        }

        ctx.callSession.disconnectFromRoom("ws_call_rejected_or_ended");
        ctx.setCallState("idle");
        ctx.callStateRef.current = "idle";
        ctx.setOutgoingCallProgress('idle');
        ctx.setViewMode("contacts");
        ctx.setSelectedUser(null);
        ctx.setCallDuration(0);
        ctx.callIdRef.current = null;
        ctx.callStartTimeRef.current = null;
        ctx.onClearIncomingCallRef.current?.();
      }
      break;
    }

    case "call_answer_failed": {
      const p = parseCallAnswerFailedPayload(envelope);
      if (!p) break;
      if (
        ctx.callStateRef.current === "initiating" ||
        ctx.callStateRef.current === "ringing"
      ) {
        ctx.localAnsweringCallIdRef.current = null;
        ctx.clearColdStartCallHandling();
        ctx.callNotificationsRef.current?.stopDialTone();
        ctx.callNotificationsRef.current?.stopRingtone();
        ctx.setOutgoingCallProgress('idle');

        const failReason =
          p.reason === "busy"
            ? "User is on another call"
            : p.error || "Call could not be connected";
        toast.error(failReason);

        ctx.setCallState("idle");
        ctx.callStateRef.current = "idle";
  ctx.setOutgoingCallProgress('idle');
        ctx.setViewMode("contacts");
        ctx.setSelectedUser(null);
        ctx.callIdRef.current = null;
        ctx.onClearIncomingCallRef.current?.();
      }
      break;
    }

    case "call_missed": {
      const p = parseCallMissedPayload(envelope);
      if (!p) break;
      if (ctx.onMissedCallsChangeRef.current) {
        ctx.onMissedCallsChangeRef.current(
          (p.missed_count ?? ctx.missedCallsCountRef.current) + 1,
        );
      }
      break;
    }

    // Pickup group events
    case "pickup_group_call_ringing": {
      const p = parsePickupGroupCallRingingPayload(envelope);
      if (!p) break;
      ctx.setPickupGroupCalls((prev) => {
        if (prev.some((c) => c.call_id === p.call_id)) return prev;
        return [
          ...prev,
          {
            call_id: p.call_id,
            caller_name: p.caller_name || "Unknown",
            caller_number: p.caller_number || "Unknown",
            ringing_extension: p.ringing_extension ?? "",
            ringing_user_name: p.ringing_user_name ?? "",
            ring_started_at: new Date(),
          },
        ];
      });
      ctx.setShowPickupIndicator(true);
      break;
    }

    case "pickup_group_call_answered":
    case "pickup_group_call_ended": {
      const p = parsePickupGroupCallLifecyclePayload(envelope);
      if (!p) break;
      let pickupListEmpty = false;
      ctx.setPickupGroupCalls((prev) => {
        const next = prev.filter((c) => c.call_id !== p.call_id);
        pickupListEmpty = next.length === 0;
        return next;
      });
      if (pickupListEmpty) {
        ctx.setShowPickupIndicator(false);
      }
      if (ctx.pickingUpCall === p.call_id) {
        ctx.setPickingUpCall(null);
      }
      break;
    }

    case "pickup_call_confirmed": {
      const p = parsePickupCallConfirmedPayload(envelope);
      if (!p) break;

      const roomName = p.livekit_room || p.room_name || p.call_id;
      const listRowId = p.call_id ?? roomName;

      ctx.setPickingUpCall(null);
      let pickupListEmptyAfter = false;
      ctx.setPickupGroupCalls((prev) => {
        const next = listRowId
          ? prev.filter((c) => c.call_id !== listRowId)
          : prev;
        pickupListEmptyAfter = next.length === 0;
        return next;
      });
      if (pickupListEmptyAfter) {
        ctx.setShowPickupIndicator(false);
      }

      if (!roomName) {
        toast.error("Call picked up, but no room was returned");
        break;
      }

      try {
        const tokenData = await ctx.getLivekitToken(roomName, "voice");
        if (!tokenData) {
          toast.error("Call picked up, but failed to get call token");
          break;
        }

        const connected = await ctx.callSession.connectToRoom(
          roomName,
          "voice",
          tokenData.token,
          tokenData.host,
        );

        if (!connected) {
          toast.error("Call picked up, but failed to connect");
          break;
        }

        ctx.callIdRef.current = roomName;
        ctx.isSipCallRef.current = true;
        ctx.sipCallLogIdRef.current = p.call_log_id || null;
        ctx.setSipCallData({
          call_log_id: p.call_log_id || undefined,
          provider_call_id: p.provider_call_id || undefined,
          livekit_room: roomName,
          caller_name: p.caller_name,
          caller_number: p.caller_number,
        });
        ctx.setSelectedUser({
          id: p.original_target_user_id || 0,
          email: "",
          first_name: p.caller_name || p.caller_number || "Picked Up Call",
          last_name: "",
          role: "",
          module_role: "",
        });
        ctx.setCallType("voice");
        ctx.setCallState("connected");
        ctx.callStateRef.current = "connected";
        ctx.callStartTimeRef.current = Date.now();
        ctx.setViewMode("incall");
        toast.success("Call picked up");
      } catch (err) {
        console.error("[TotalConnectRTC] Pickup connect failed:", err);
        toast.error("Failed to connect picked-up call");
      }
      break;
    }

    // Parking events
    case "call_parked": {
      const p = parseCallParkedPayload(envelope);
      if (!p) break;
      if (ctx.showParkingDialog) ctx.loadParkingSlots();
      toast(
        `Call parked on slot ${p.parking_slot ?? "?"} by ${p.parked_by ?? "Unknown"}`,
        {
          icon: "📞",
        },
      );
      break;
    }

    case "parked_call_retrieved": {
      if (!parseParkedCallRetrievedPayload(envelope)) break;
      if (ctx.showParkingDialog) ctx.loadParkingSlots();
      break;
    }

    // Recording events from other participants
    case "call_recording_started": {
      const p = parseCallRecordingStartedPayload(envelope);
      if (!p) break;
      if (p.user_id !== ctx.userId) {
        toast(`${p.user_name ?? "Someone"} started recording`, { icon: "🔴" });
      }
      break;
    }

    case "call_recording_stopped": {
      const p = parseCallRecordingStoppedPayload(envelope);
      if (!p) break;
      if (p.user_id !== ctx.userId) {
        toast(`${p.user_name ?? "Someone"} stopped recording`, { icon: "⏹️" });
      }
      break;
    }

    // Whiteboard drawing from another participant
    case "call_whiteboard_draw": {
      const p = parseCallWhiteboardDrawPayload(envelope);
      if (!p) break;
      const el = p.element;
      if (p.user_id && p.user_id !== ctx.userId && el) {
        ctx.setWhiteboardElements((prev) => [...prev, el]);
      }
      break;
    }

    case "call_whiteboard_status": {
      const p = parseCallWhiteboardStatusPayload(envelope);
      if (!p) break;
      const uid = p.user_id !== undefined ? p.user_id : Number.NaN;
      const myId = ctx.userId;
      if (!Number.isFinite(uid) || myId === undefined || uid === myId) break;

      if (p.is_open) {
        const uname =
          typeof p.user_name === "string" ? p.user_name : "Participant";
        if (ctx.whiteboardUIModeRef.current.kind === "presenting") {
          ctx.sendWhiteboardStatus(false);
        }
        ctx.setRemotePresenterInfo({ userId: uid, userName: uname });
        ctx.setWhiteboardUIMode({
          kind: "following",
          userId: uid,
          userName: uname,
        });
        ctx.setShowWhiteboard(true);
      } else {
        ctx.setRemotePresenterInfo((prev) =>
          prev?.userId === uid ? null : prev,
        );
        ctx.setWhiteboardUIMode((prev) => {
          if (prev.kind === "following" && prev.userId === uid) {
            ctx.setShowWhiteboard(false);
            return { kind: "idle" };
          }
          if (prev.kind === "presenting") {
            /* Another participant ended their session — keep our canvas if presenting */
            return prev;
          }
          return prev;
        });
      }
      break;
    }

    // Breakout rooms started by host
    case "breakout_rooms_started": {
      const p = parseBreakoutRoomsStartedPayload(envelope);
      if (!p) break;
      if (p.rooms) {
        ctx.setBreakoutRooms(p.rooms);
        ctx.setIsBreakoutActive(true);
        // Find which room current user is in
        for (const room of p.rooms) {
          const found = room.participants.find((bp) => bp.id === ctx.userId);
          if (found) {
            ctx.setCurrentBreakoutRoom(room.id);
            break;
          }
        }
        toast.success("Breakout rooms have started");
      }
      break;
    }

    // Breakout rooms ended by host
    case "breakout_rooms_ended": {
      if (!parseBreakoutRoomsEndedPayload(envelope)) break;
      ctx.setBreakoutRooms([]);
      ctx.setIsBreakoutActive(false);
      ctx.setCurrentBreakoutRoom(null);
      toast("Breakout rooms have ended", { icon: "🔄" });
      break;
    }

    // Breakout room participant list updated
    case "breakout_room_updated": {
      const p = parseBreakoutRoomUpdatedPayload(envelope);
      if (!p) break;
      if (p.room_id && p.participants) {
        const { room_id, participants } = p;
        ctx.setBreakoutRooms((prev) =>
          prev.map((room) =>
            room.id === room_id ? { ...room, participants } : room,
          ),
        );
      }
      break;
    }

    case "incoming_group_call": {
      const gp = parseIncomingGroupCallPayload(envelope);
      if (!gp) break;

      const groupCallType: CallType =
        gp.call_type === "video" ? "video" : "voice";

      // Handle incoming group call notification
      if (
        ctx.callStateRef.current === "idle" ||
        ctx.callStateRef.current === "ended"
      ) {
        ctx.callNotificationsRef.current?.cancelCallNotification();

        ctx.setIsGroupCall(true);
        ctx.setGroupCallInfo({
          threadId: gp.thread_id ?? "",
          threadName: gp.thread_name || "Group Call",
          participantCount: gp.participant_count,
          callerName: gp.caller_name,
          callType: groupCallType,
        });

        if (!ctx.isOpenRef.current && ctx.onIncomingCallRef.current) {
          ctx.onIncomingCallRef.current({
            call_id: gp.call_id,
            caller_id: gp.caller_id,
            caller_name: `${gp.thread_name || "Group Call"}`,
            caller_avatar: gp.caller_avatar,
            call_type: groupCallType,
            timestamp: gp.timestamp || new Date().toISOString(),
          });
        }

        ctx.callIdRef.current = gp.call_id;
        (window as TotalConnectRtcCallWindowExtension).__pendingGroupCallData =
          {
            threadId: gp.thread_id,
            threadName: gp.thread_name,
            participantCount: gp.participant_count,
          };

        ctx.setSelectedUser({
          id: gp.caller_id,
          email: "",
          first_name: gp.thread_name || "Group Call",
          last_name: "",
          role: "",
          module_role: "",
          avatar_url: undefined,
        });

        ctx.setCallType(groupCallType);
        ctx.setViewMode("incall");
        ctx.callStateRef.current = "ringing";
        ctx.setCallState("ringing");
        ctx.callNotificationsRef.current?.playRingtone();

        if (ctx.isTauriDesktop) {
          ctx.focusWindow();
        }
      } else {
      }
      break;
    }

    case "call_hand_raise": {
      const p = parseCallHandRaisePayload(envelope);
      if (!p) break;
      const uid = p.user_id;
      if (uid && uid !== ctx.userId) {
        ctx.setParticipantHandRaises((prev) => {
          const newMap = new Map(prev);
          if (p.is_raised) {
            newMap.set(uid, true);
          } else {
            newMap.delete(uid);
          }
          return newMap;
        });
        ctx.addActivityEntry(
          p.is_raised ? "hand_raised" : "hand_lowered",
          p.user_name || "Participant",
          uid,
        );
      }
      break;
    }

    case "call_reaction": {
      const p = parseCallReactionPayload(envelope);
      if (!p) break;
      if (p.user_id && p.user_id !== ctx.userId && p.emoji) {
        const newReaction = {
          id: p.reaction_id || `reaction-${Date.now()}`,
          emoji: p.emoji,
          userId: p.user_id,
          userName: p.user_name || "Participant",
          timestamp: Date.now(),
        };
        ctx.setActiveReactions((prev) => [...prev, newReaction]);
        setTimeout(() => {
          ctx.setActiveReactions((prev) =>
            prev.filter((r) => r.id !== newReaction.id),
          );
        }, 3000);
        ctx.callNotificationsRef.current?.hapticFeedback("light");
      }
      break;
    }

    case "participant_mute_request": {
      const p = parseParticipantMuteRequestPayload(envelope);
      if (!p) break;
      if (p.target_id === ctx.userId) {
        toast(
          (t) => (
            <div className="flex flex-col gap-2">
              <span className="font-medium">
                {p.requester_name || "Host"} asks you to mute
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    ctx.callControls.toggleMute();
                    toast.dismiss(t.id);
                  }}
                  className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Mute Now
                </button>
                <button
                  onClick={() => toast.dismiss(t.id)}
                  className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Ignore
                </button>
              </div>
            </div>
          ),
          {
            duration: 10000,
            icon: "🔇",
          },
        );
        ctx.callNotificationsRef.current?.hapticFeedback("medium");
      }
      break;
    }

    case "participant_removed": {
      const p = parseParticipantRemovedPayload(envelope);
      if (!p) break;
      if (p.target_id === ctx.userId) {
        toast.error(
          `You were removed from the call by ${p.requester_name || "the host"}`,
          { duration: 5000 },
        );
        ctx.callNotificationsRef.current?.hapticFeedback("heavy");
        ctx.endCallRef.current?.("removed_by_host");
      }
      break;
    }

    case "lobby_request": {
      const p = parseLobbyRequestPayload(envelope);
      if (!p) break;
      const meetingId = p.meeting_id;
      const guestIdentity = p.guest_identity;
      const guestName = p.guest_name;
      if (meetingId && guestIdentity && guestName) {
        ctx.setWaitingParticipants((prev) => {
          if (prev.find((gp) => gp.identity === guestIdentity)) return prev;
          return [
            ...prev,
            {
              identity: guestIdentity,
              name: guestName,
              userId: 0,
              isMuted: false,
              isSpeaking: false,
              hasVideo: false,
            },
          ];
        });
        toast(`${guestName} is waiting to join`, { icon: "🚪" });
        ctx.callNotificationsRef.current?.hapticFeedback("light");
      }
      break;
    }

    case "guest_left_lobby": {
      const p = parseGuestLeftLobbyPayload(envelope);
      if (!p) break;
      if (p.guest_identity) {
        ctx.setWaitingParticipants((prev) =>
          prev.filter((gp) => gp.identity !== p.guest_identity),
        );
      }
      break;
    }

    case "new_voicemail": {
      const p = parseNewVoicemailPayload(envelope);
      if (!p) break;
      toast(
        `New voicemail from ${p.caller_number || "Unknown"}${p.duration_seconds ? ` (${p.duration_seconds}s)` : ""}`,
        {
          icon: "📬",
          duration: 6000,
        },
      );
      break;
    }
  }
}
